import React from 'react';

export const pluralize = (count, singular, plural) => {
    return count === 1 ? singular : (plural || `${singular}s`);
};

/**
 * Get modal configuration for different action types
 * @param {string} action - Action type ('deleteJob', 'deleteApplicant', 'send', 'close', 'reopen', 'unsavedQuestions')
 * @param {number} count - Number of items
 * @param {string} item - Singular item type ('job', 'applicant', etc.)
 * @param {string} itemName - Specific item name/title
 * @param {string} [customPlural] - Optional custom plural form
 */
export const getModalConfig = (action, count, item, itemName, customPlural) => {
    const configs = {
        deleteJob: {
            title: `Delete ${pluralize(count, item, customPlural)}`,
            confirmButtonText: `Yes, Delete ${pluralize(count, item, customPlural)}`,
            details: count === 1
                ? `This will remove ${itemName || (item || '').toLowerCase()} from your active jobs list. The job and all associated data will be preserved for audit purposes. If this job is from your ATS, it will not be deleted from your ATS system and may reappear when you refresh your ATS integration.`
                : `This will remove ${count} ${pluralize(count, item, customPlural).toLowerCase()} from your active jobs list. The jobs and all associated data will be preserved for audit purposes. If any of these jobs are from your ATS, they will not be deleted from your ATS system and may reappear when you refresh your ATS integration.`,
        },
        deleteApplicant: {
            title: `Delete ${pluralize(count, item, customPlural)}`,
            confirmButtonText: `Yes, Delete ${pluralize(count, item, customPlural)}`,
            details: count === 1
                ? `This will remove ${itemName || (item || '').toLowerCase()} from your active applicants list. The applicant and all associated data will be preserved for audit purposes.`
                : `This will remove ${count} ${pluralize(count, item, customPlural).toLowerCase()} from your active applicants list. The applicants and all associated data will be preserved for audit purposes.`,
        },
        send: {
            title: `Send ${pluralize(count, item, customPlural)}`,
            confirmButtonText: `Yes, Send ${pluralize(count, item, customPlural)}`,
            details: count === 1
                ? `This will send an ${(item || '').toLowerCase()} invitation to ${itemName || 'the candidate'}.`
                : `This will send ${(item || '').toLowerCase()} invitations to ${count} candidates.`,
        },
        close: {
            title: count === 1 
                ? `Disable Follow-up Messages`
                : `Disable Follow-up Messages for ${count} ${pluralize(count, item, customPlural)}`,
            confirmButtonText: count === 1
                ? `Yes, Disable Follow-up Messages`
                : `Yes, Disable Follow-up Messages for ${count} ${pluralize(count, item, customPlural)}`,
            details: count === 1
                ? `This will stop follow-up messages and prevent automatic evaluation for ${itemName || 'this candidate'}. The candidate can still complete their current interview if they are in progress.`
                : `This will stop follow-up messages and prevent automatic evaluation for ${count} interviews. Candidates can still complete their current interviews if they are in progress.`,
        },
        reopen: {
            title: count === 1
                ? `Enable Follow-up Messages`
                : `Enable Follow-up Messages for ${count} ${pluralize(count, item, customPlural)}`,
            confirmButtonText: count === 1
                ? `Yes, Enable Follow-up Messages`
                : `Yes, Enable Follow-up Messages for ${count} ${pluralize(count, item, customPlural)}`,
            details: count === 1
                ? `This will resume follow-up messages and trigger evaluation if the interview is completed for ${itemName || 'this candidate'}.`
                : `This will resume follow-up messages and trigger evaluation if completed for ${count} interviews.`,
        },
        resend: {
            title: `Resend Interview?`,
            confirmButtonText: `Yes, Resend Interview`,
            details: `This applicant already has an interview. Resending will create a new interview session and permanently delete the existing transcript, responses, and evaluation. This action cannot be undone. Are you sure you want to continue?`,
        },
        resendUpdatedQuestions: {
            title: `Restart interview with new questions?`,
            confirmButtonText: `Continue`,
            details: `${itemName || 'This candidate'}'s existing transcript, responses, and evaluation will be permanently deleted. The candidate will be texted a fresh interview using the current questions.`,
        },
        resendUpdatedQuestionsFinal: {
            title: `This cannot be undone. Restart ${itemName || 'this candidate'}'s interview now?`,
            confirmButtonText: `Yes, restart interview`,
            details: `The existing interview data will be permanently deleted.`,
        },
        unsavedQuestions: {
            title: `Unsaved Interview Questions`,
            confirmButtonText: `Go back and save`,
            cancelButtonText: `Continue without saving`,
            details: `You have unsaved interview questions. If you continue, your changes will be lost. Go back to review and save your questions before leaving.`,
            dataCyConfirm: `unsaved-questions-go-back`,
            dataCyCancel: `unsaved-questions-continue-without-saving`,
        },
        unsavedRoleDetails: {
            title: `Unsaved Role Details`,
            confirmButtonText: `Go back and save`,
            cancelButtonText: `Continue without saving`,
            details: `You have unsaved role details. If you continue, your changes will be lost. Go back to review and save before leaving.`,
            dataCyConfirm: `unsaved-role-details-go-back`,
            dataCyCancel: `unsaved-role-details-continue-without-saving`,
        },
        unsavedChanges: {
            title: `Unsaved changes`,
            confirmButtonText: `Go back and save`,
            cancelButtonText: `Continue without saving`,
            details: `You have unsaved role details and interview questions. If you continue, your changes will be lost.`,
            dataCyConfirm: `unsaved-changes-go-back`,
            dataCyCancel: `unsaved-changes-continue-without-saving`,
        },
        roleSetupRequired: {
            title: `Finish setting up this role`,
            confirmButtonText: `Set up the role`,
            cancelButtonText: `Not now`,
            details: `Automatic interviews turn on after this role has interview questions and evaluation rules.`,
        },
        enableAutomaticInterviews: {
            title: `Enable automatic interviews?`,
            confirmButtonText: `Yes, enable automatic interviews`,
        },
        analysisRubricRequired: {
            title: `Evaluation criteria required`,
            confirmButtonText: `Go to Evaluation Criteria`,
            cancelButtonText: `Not now`,
            details: `Create and save this role's Evaluation Criteria before sending interviews or enabling automatic interviews. This ensures every completed interview can be scored consistently.`,
        },
        generateQuestions: {
            title: `What kind of questions should we generate?`,
            confirmButtonText: `Generate Questions`,
        },
        retryAnalysis: {
            title: count === 1 ? `Retry evaluation?` : `Retry evaluation for ${count} ${pluralize(count, item, customPlural)}?`,
            confirmButtonText: `Retry evaluation only`,
            secondaryActionText: `Retry and update ATS stages`,
            details: (() => {
                const subject = count === 1
                    ? (itemName || "this applicant")
                    : `${count} ${pluralize(count, item, customPlural).toLowerCase()}`;
                const candidateWording = count === 1
                    ? "the candidate"
                    : "candidates";
                return (
                    <div className="space-y-4">
                        <p>
                            This will replace the current evaluation for {subject} and{" "}
                            <span className="font-bold">cannot be undone</span>.
                        </p>
                        <p>
                            If you also update ATS stages, the new scores may move {candidateWording}{" "}
                            to a different stage, depending on your settings. That stage
                            change can fire automated messages or other actions from your
                            ATS.
                        </p>
                    </div>
                );
            })(),
        },
    };

    return configs[action] || {
        title: `Confirm Action`,
        confirmButtonText: `Yes, Confirm`,
        details: '',
    };
};
